import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Truck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShippingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState(6.99); // Auto-set to flat rate
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'CA',
    phone: '',
    email: ''
  });

  const cartItems = JSON.parse(localStorage.getItem('cart') || '[]');
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const tax = subtotal * 0.13;

  const applyCoupon = () => {
    const code = couponCode.toUpperCase().trim();
    
    // Valid coupon codes
    const coupons = {
      'FREESHIP': { type: 'free_shipping', discount: 0, description: 'Free Shipping' },
      'WELCOME10': { type: 'percent', discount: 10, description: '10% Off' },
      'SAVE5': { type: 'fixed', discount: 5, description: '$5 Off Shipping' }
    };

    if (coupons[code]) {
      setCouponApplied(coupons[code]);
      toast.success(`Coupon applied: ${coupons[code].description}`);
    } else {
      toast.error('Invalid coupon code');
      setCouponApplied(null);
    }
  };

  const calculateFinalShipping = () => {
    if (!couponApplied) return shippingCost;
    
    if (couponApplied.type === 'free_shipping') {
      return 0;
    } else if (couponApplied.type === 'fixed') {
      return Math.max(0, shippingCost - couponApplied.discount);
    } else if (couponApplied.type === 'percent') {
      return shippingCost * (1 - couponApplied.discount / 100);
    }
    
    return shippingCost;
  };

  const finalShippingCost = calculateFinalShipping();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Reset shipping cost when address changes
    if (['city', 'state', 'zip', 'country'].includes(e.target.name)) {
      setShippingCost(null);
    }
  };

  const handleCheckout = async () => {

    if (!formData.email || !formData.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Create Stripe session with shipping cost as line item
      const lineItems = [
        ...cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          discount: item.discount || 0,
          tier: item.tier || 'Retail'
        }))
      ];

      // Add shipping as a line item (with coupon applied if any)
      if (finalShippingCost > 0) {
        lineItems.push({
          id: 'shipping',
          name: couponApplied ? `Shipping (${couponApplied.description} applied)` : 'Shipping',
          price: finalShippingCost,
          quantity: 1,
          discount: 0,
          tier: 'Shipping'
        });
      } else if (couponApplied && couponApplied.type === 'free_shipping') {
        // Show free shipping as $0
        lineItems.push({
          id: 'shipping',
          name: 'Shipping (FREE SHIP applied)',
          price: 0,
          quantity: 1,
          discount: 0,
          tier: 'Shipping'
        });
      }

      const response = await axios.post(`${API}/checkout/create-session`, {
        items: lineItems,
        customer_email: formData.email,
        shipping_address: {
          name: formData.name,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          phone: formData.phone
        }
      });

      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to create checkout session');
      setLoading(false);
    }
  };

  const total = subtotal + tax + finalShippingCost;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <button onClick={() => navigate('/')} className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-lighting/artifacts/27f7yllt_WhatsApp%20Image%202025-10-18%20at%2008.58.35.jpeg" 
                alt="ONYX Home Solutions" 
                className="h-12 lg:h-16 w-auto object-contain"
              />
            </button>
            <div className="flex items-center gap-4">
              <Truck className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-600">Calculate Shipping</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-20 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <button 
            onClick={() => navigate('/cart')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Cart
          </button>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Shipping Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl p-8 shadow-xl">
                <h2 className="text-3xl font-bold mb-8 text-gray-900">Shipping Address</h2>
                
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                      <input 
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-500"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                      <input 
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-500"
                        placeholder="John Smith"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Address *</label>
                    <input 
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-500"
                      placeholder="123 Main St"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
                      <input 
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-500"
                        placeholder="Toronto"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">State/Province *</label>
                      <input 
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-500"
                        placeholder="ON"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">ZIP/Postal Code *</label>
                      <input 
                        type="text"
                        name="zip"
                        value={formData.zip}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-500"
                        placeholder="M5H 2N2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Country *</label>
                      <select 
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-500"
                      >
                        <option value="CA">Canada</option>
                        <option value="US">United States</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
                    <input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-amber-500"
                      placeholder="(416) 555-1234"
                    />
                  </div>

                  {/* Coupon Code Section */}
                  <div className="mt-6 p-6 bg-amber-50 rounded-2xl border-2 border-amber-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Have a Coupon Code?</h3>
                    <div className="flex gap-2">
                      <input 
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="flex-1 px-4 py-3 border-2 border-amber-300 rounded-xl focus:outline-none focus:border-amber-500 uppercase"
                      />
                      <Button 
                        onClick={applyCoupon}
                        className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-xl"
                      >
                        Apply
                      </Button>
                    </div>
                    {couponApplied && (
                      <div className="mt-3 text-sm text-green-700 font-semibold">
                        ✓ {couponApplied.description} applied!
                      </div>
                    )}
                    <div className="mt-3 text-xs text-gray-600">
                      Valid codes: FREESHIP, WELCOME10, SAVE5
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-3xl p-8 shadow-xl sticky top-32">
                <h2 className="text-2xl font-bold mb-6 text-gray-900">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-semibold">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax (13%)</span>
                    <span className="font-semibold">${tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-semibold">
                      {couponApplied && shippingCost !== finalShippingCost ? (
                        <span className="space-x-2">
                          <span className="line-through text-gray-400">${shippingCost.toFixed(2)}</span>
                          <span className="text-green-600">${finalShippingCost.toFixed(2)}</span>
                        </span>
                      ) : (
                        <span>${finalShippingCost.toFixed(2)}</span>
                      )}
                    </span>
                  </div>
                  {couponApplied && (
                    <div className="flex justify-between text-green-600">
                      <span className="text-sm">Coupon ({couponCode})</span>
                      <span className="text-sm font-semibold">
                        {couponApplied.type === 'free_shipping' && 'FREE SHIPPING'}
                        {couponApplied.type === 'fixed' && `-$${couponApplied.discount.toFixed(2)}`}
                        {couponApplied.type === 'percent' && `-${couponApplied.discount}%`}
                      </span>
                    </div>
                  )}
                </div>

                <div className="pt-6 border-t border-gray-200 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold">Total</span>
                    <span className="text-3xl font-bold text-amber-600">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>

                <Button 
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-amber-600 to-amber-700 text-white py-5 rounded-full text-lg font-medium disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Continue to Payment'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPage;
